import { type PropsWithChildren, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spin } from 'antd';
import { authApi, surveyQueries } from '@/services';
import { useNotification } from '@/context/NotificationContext.tsx';
import { useFirebaseNotifications } from '@/hooks/useFirebaseNotifications.tsx';
import { WelcomeWizardModal } from '@/features/client/onboarding/components/WelcomeWizard';
import { Sentry } from '@/config/sentry';

export const PrivateLayer = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setUserId } = useNotification();

  // Raised by the axios interceptors on any 403; refetching the user lets
  // MainLayout redirect to the right area for whoever is actually signed in.
  useEffect(() => {
    const recheck = () => queryClient.refetchQueries({ queryKey: ['currentUser'] });
    window.addEventListener('whm:forbidden', recheck);
    return () => window.removeEventListener('whm:forbidden', recheck);
  }, [queryClient]);

  const { data: userData, isFetched } = useQuery({
    queryKey: ['currentUser'],
    queryFn: authApi.getCurrentUser,
    retry: false,
    // Re-check the session when the tab regains focus: a login as another
    // user in a second tab swaps the cookie under this one.
    refetchOnWindowFocus: true,
  });

  const isAuthResolved = isFetched && !!userData;

  // Only hit the auth-protected FCM endpoints once the user is confirmed.
  useFirebaseNotifications(isAuthResolved);

  // Prefetches the survey; its enabled flag flips the moment the onboarding
  // wizard completes the profile, so the survey is ready on handoff.
  useQuery({
    ...surveyQueries.getCurrentSurvey(),
    enabled: userData?.role === 'CLIENT' && userData?.profile?.profile_completed,
  });

  useEffect(() => {
    if (userData) setUserId(userData.id);
  }, [setUserId, userData]);

  // Attribute errors to an account (id + role only, no PII) so 403s and
  // crashes in Sentry can be traced to a user.
  useEffect(() => {
    Sentry.setUser(userData ? { id: String(userData.id), role: userData.role } : null);
  }, [userData]);

  useEffect(() => {
    if (isFetched && !userData) {
      navigate('/public/login');
    }
  }, [isFetched, navigate, userData]);

  if (!isAuthResolved) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
        }}
      >
        <Spin size='large' />
      </div>
    );
  }

  return (
    <>
      {children}
      {userData.role === 'CLIENT' && <WelcomeWizardModal />}
    </>
  );
};
